// /Users/donovan68/dva-frontend-vite/src/pages/freemium/UpgradeToProPage.tsx
// UPDATED: New layout - Page header above journey bar, consistent mb-6 spacing

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check, X, Sparkles, Lock, Loader2, AlertCircle, CheckCircle2
} from "lucide-react";
import { FreemiumJourneyBarV8 } from "../../components/FreemiumJourneyBarV8";
import FreemiumJourneyHeader from "../../components/freemium/FreemiumJourneyHeader";
import { usePageContext } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/AuthContext";
import { getPricingOptions, createCheckoutSession } from "../../services/sessionsApiService";

export default function UpgradeToProPage() {
  const navigate = useNavigate();
  const { setPageContext } = usePageContext();

  // Auth context
  const { currentUser, session, entitlement, sessionLoading, entitlementLoading } = useAuth();

  // Pricing state
  const [pricingOptions, setPricingOptions] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState(null);

  // Billing selection
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('MONTHLY');

  // Checkout state
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Set page context - empty breadcrumbs to hide global header tabs
  useEffect(() => {
    setPageContext({
      breadcrumbs: [], // Empty to hide tabs in global header (moved to page-level)
      tabs: ["Tools", "Documents", "Messages"]
    });
  }, [setPageContext]);

  // Fetch pricing options on mount
  useEffect(() => {
    const fetchPricing = async () => {
      if (!currentUser) {
        setPricingLoading(false);
        return;
      }

      try {
        setPricingLoading(true);
        setPricingError(null);

        // Get Firebase auth token
        const token = await currentUser.getIdToken();
        const response = await getPricingOptions(token);

        // Handle response structure (check for nested data object)
        const pricesArray = response.data?.prices || response.prices || [];

        // Transform array of prices into structured object
        // Filter for TIER_2 (Pro plan) and organize by billing cycle
        const tier2Prices = pricesArray.filter((p: any) => p.plan_id === 'TIER_2');
        const monthlyPrice = tier2Prices.find((p: any) => p.billing_cycle === 'MONTHLY');
        const annualPrice = tier2Prices.find((p: any) => p.billing_cycle === 'ANNUAL');

        const transformedPricing = {
          monthly: monthlyPrice ? {
            price: (monthlyPrice.price / 100).toFixed(0), // Convert cents to dollars
            price_id: monthlyPrice.price_id,
            currency: monthlyPrice.currency
          } : null,
          annual: annualPrice ? {
            price: (annualPrice.price / 100).toFixed(0), // Convert cents to dollars
            price_id: annualPrice.price_id,
            currency: annualPrice.currency,
            savings: monthlyPrice && annualPrice
              ? ((monthlyPrice.price * 12 - annualPrice.price) / 100).toFixed(0)
              : null
          } : null
        };

        setPricingOptions(transformedPricing);
      } catch (err) {
        console.error('Failed to load pricing:', err);
        setPricingError('Failed to load pricing options. Please try again.');
      } finally {
        setPricingLoading(false);
      }
    };

    fetchPricing();
  }, [currentUser]);

  // TODO: Replace with real data from hooks/services
  const personalizationData = {
    strategy: 75,
    capabilities: 70,
    readiness: 80,
    docsCount: 3
  };

  const handleStripeCheckout = async () => {
    // Validation
    if (!currentUser) {
      setCheckoutError('You must be logged in to upgrade.');
      return;
    }

    if (!session?.client_id) {
      setCheckoutError('Client ID not found. Please try again.');
      return;
    }

    if (entitlement?.plan_id !== 'TIER_1') {
      setCheckoutError('You are already on a paid plan.');
      return;
    }

    // Get the price_id for the selected billing cycle
    const selectedPrice = selectedBillingCycle === 'MONTHLY'
      ? pricingOptions?.monthly
      : pricingOptions?.annual;

    if (!selectedPrice?.price_id) {
      setCheckoutError('Pricing information not available. Please refresh and try again.');
      return;
    }

    try {
      setCheckoutLoading(true);
      setCheckoutError(null);

      // Get Firebase auth token
      const token = await currentUser.getIdToken();

      // Construct redirect URLs
      const baseUrl = window.location.origin;
      const checkoutData = {
        plan_id: 'TIER_2',
        billing_cycle: selectedBillingCycle,
        price_id: selectedPrice.price_id, // Use the price_id from API
        success_url: `${baseUrl}/subscription/success`,
        cancel_url: `${baseUrl}/subscription/cancelled`,
      };

      const response = await createCheckoutSession(session.client_id, checkoutData, token);

      // Redirect to Stripe Checkout
      const checkoutUrl = response.data?.checkout_url || response.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        console.error('API response:', response);
        throw new Error('No checkout URL returned from API');
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      setCheckoutError(err.message || 'Failed to initiate checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Show loading while auth is initializing
  if (sessionLoading || entitlementLoading) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF6E4C] mx-auto mb-3" />
            <p className="text-sm text-[#717182]">Loading...</p>
          </div>
        </div>
      </main>
    );
  }

  // Show message if already on paid plan
  if (entitlement?.plan_id !== 'TIER_1') {
    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#18365E] mb-3">
              You're Already a Pro Member!
            </h2>
            <p className="text-[#717182] mb-6">
              You already have access to all DVA Pro features.
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-3 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-all"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </main>
    );
  }

  // Show message if user is CLIENT_USER or CLIENT_EARLY_ADOPTER (not admin)
  if (session?.role === 'CLIENT_USER' || session?.role === 'CLIENT_EARLY_ADOPTER') {
    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <Lock className="w-16 h-16 text-[#FF6E4C] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#18365E] mb-3">
              Upgrade Requires Administrator Access
            </h2>
            <p className="text-[#717182] mb-6">
              Only administrators can upgrade your organization's plan. Please contact your administrator to request an upgrade to DVA Pro.
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-3 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-all"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-4">
      
      {/* Step Header with Tabs */}
      <FreemiumJourneyHeader
        stepNumber={4}
        stepName="Upgrade to DVA Pro"
        description="Unlock CFO-grade ROI modeling, initiative prioritization, automated governance tracking, and execution-ready strategic outputs to transform your data strategy into measurable business value."
      />

      {/* Journey Bar - Always Freemium */}
      <div className="mb-6">
        <FreemiumJourneyBarV8 
          currentStep={4} 
          isPersonalized={true}
          personalizationData={personalizationData}
          onPersonalizationClick={() => navigate("/personalize")}
          onStepClick={(stepId) => {
            if (stepId === 1) navigate("/home");
            else if (stepId === 2) navigate("/personalize");
            else if (stepId === 3) navigate("/freemium/strategy");
            else if (stepId === 4) navigate("/freemium/upgrade");
          }}
        />
      </div>

      {/* 3. Content */}
      
      {/* Hero Section */}
      <section className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-6 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="max-w-xl">
          <h2 className="text-xl font-bold text-[#18365E] mb-2">
            Unlock Advanced Analytics & ROI Intelligence
          </h2>
          <p className="text-sm text-[#475569] mb-3">
            Gain CFO-grade ROI modeling, initiative prioritization, automated governance tracking,
            and execution-ready strategic outputs.
          </p>
          <ul className="space-y-1 text-[11px] text-[#475569]">
            <li>• Quantify the financial impact of each analytics initiative.</li>
            <li>• Turn your Data Product Playbook into an executable roadmap.</li>
            <li>• Provide executives with clear, CFO-ready decision support.</li>
          </ul>
        </div>

        {/* Optional right-side badge */}
        <div className="flex flex-col items-start md:items-end gap-2">
          <p className="text-[11px] uppercase tracking-wide text-[#64748b] font-semibold">
            DVA Pro adds
          </p>
          <div className="rounded-xl bg-[#f1f5f9] px-4 py-3 text-xs text-[#475569]">
            <p className="font-semibold text-[#18365E] mb-1">
              Advanced ROI, Roadmaps & Governance
            </p>
            <p>Built on your personalized insights and Data Product Playbook.</p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="mb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1000px] mx-auto">
          {/* Freemium Card */}
          <div className="rounded-2xl ring-1 ring-[#e5e7eb] bg-white p-8">
            <h3 className="text-2xl font-bold text-[#18365E] mb-2">Freemium</h3>
            <p className="text-sm text-[#717182] mb-6 font-medium">Perfect for exploring DVA</p>
            
            <div className="mb-6">
              <span className="text-4xl font-bold text-[#18365E]">$0</span>
              <span className="text-[#717182] font-medium">/month</span>
            </div>

            <button
              onClick={() => navigate("/home")}
              className="w-full py-3 rounded-xl font-semibold text-[#18365E] bg-white ring-1 ring-[#e5e7eb] hover:ring-[#18365E] transition-all mb-6"
            >
              Current Plan
            </button>

            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#717182] font-medium">Industry insights & benchmarks</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#717182] font-medium">Basic personalization</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#717182] font-medium">Strategy overview</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-400 font-medium">ROI modeling</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-400 font-medium">Roadmap builder</span>
              </div>
            </div>
          </div>

          {/* Pro Card */}
          <div className="rounded-2xl ring-2 ring-[#FF6E4C] bg-gradient-to-br from-white to-[#FF6E4C]/5 p-8 relative shadow-xl">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF6E4C] text-white text-xs px-4 py-1 rounded-full font-semibold">
              MOST POPULAR
            </div>

            <h3 className="text-2xl font-bold text-[#18365E] mb-2">DVA Pro</h3>
            <p className="text-sm text-[#717182] mb-6 font-medium">Complete data strategy platform</p>

            {pricingLoading ? (
              <div className="mb-6 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#18365E]" />
                <span className="text-sm text-[#717182]">Loading pricing...</span>
              </div>
            ) : pricingError ? (
              <div className="mb-6">
                <span className="text-sm text-red-600">{pricingError}</span>
              </div>
            ) : pricingOptions ? (
              <>
                {/* Billing cycle selector */}
                <div className="mb-4 flex gap-2 p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setSelectedBillingCycle('MONTHLY')}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                      selectedBillingCycle === 'MONTHLY'
                        ? 'bg-white text-[#18365E] shadow-sm'
                        : 'text-[#717182] hover:text-[#18365E]'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setSelectedBillingCycle('ANNUAL')}
                    className={`flex-1 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                      selectedBillingCycle === 'ANNUAL'
                        ? 'bg-white text-[#18365E] shadow-sm'
                        : 'text-[#717182] hover:text-[#18365E]'
                    }`}
                  >
                    Annual
                  </button>
                </div>

                {/* Dynamic price display */}
                <div className="mb-6">
                  <span className="text-4xl font-bold text-[#18365E]">
                    ${selectedBillingCycle === 'MONTHLY'
                      ? pricingOptions.monthly?.price || '399'
                      : pricingOptions.annual?.price || '3990'}
                  </span>
                  <span className="text-[#717182] font-medium">
                    /{selectedBillingCycle === 'MONTHLY' ? 'month' : 'year'}
                  </span>
                  {selectedBillingCycle === 'ANNUAL' && pricingOptions.annual?.savings && (
                    <p className="text-sm text-green-600 font-semibold mt-1">
                      Save ${pricingOptions.annual.savings}/year
                    </p>
                  )}
                </div>
              </>
            ) : null}

            {/* Error display */}
            {checkoutError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{checkoutError}</p>
              </div>
            )}

            <button
              onClick={handleStripeCheckout}
              disabled={checkoutLoading || pricingLoading || sessionLoading || entitlementLoading}
              className="w-full py-3 rounded-xl font-semibold text-white bg-[#FF6E4C] hover:bg-[#ff5a3a] transition-all shadow-lg hover:shadow-xl mb-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Redirecting to checkout...
                </>
              ) : (
                'Get Started with Pro →'
              )}
            </button>

            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#18365E] font-semibold">Everything in Freemium, plus:</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#717182] font-medium">Advanced ROI modeling</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#717182] font-medium">Interactive roadmap builder</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#717182] font-medium">Governance tracking</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#717182] font-medium">Executive reports</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#717182] font-medium">Priority support</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}